import { useState, useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import Cookie from "universal-cookie";
import { useAuth } from "@/contexts/AuthContext";
import { Group, Text, Combobox, InputBase, Input, useCombobox, Stack, Divider } from "@mantine/core";
import SVGAsset from "@/components/atoms/SVGAsset/SVGAsset";
import { CurrentCompany } from "@/types";
import Modal from "@/components/molecules/Modal";

function SelectOption(props: CurrentCompany & { highlight?: boolean }) {
  return (
    <Group gap={8} wrap="nowrap" style={{ minWidth: 0, maxWidth: "100%", overflow: "hidden" }}>
      <SVGAsset name={"company-icon"} className="circle" style={{ flexShrink: 0 }} />
      <Text
        fw={500}
        truncate
        style={{
          minWidth: 0,
          maxWidth: "100%",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {props.companyName}
      </Text>
    </Group>
  );
}

export default function SelectCompanyOnLogin() {
  const { t } = useTranslation();
  const { companies, changeCompany } = useAuth();
  const [isCompanyChoiceOpen, setIsCompanyChoiceOpen] = useState(false);
  const [value, setValue] = useState<string | null>(null);
  const cookies = useMemo(() => new Cookie(), []);
  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
  });

  const RECENT_COOKIE_KEY = "recentCompanies";
  const MAX_RECENT = 3;

  const getRecentCompanies = () => {
    const arr = cookies.get(RECENT_COOKIE_KEY);
    if (!Array.isArray(arr)) return [];
    return arr;
  };

  const setRecentCompanies = (companyId: string) => {
    let arr = getRecentCompanies();
    arr = [companyId, ...arr.filter((id) => id !== companyId)];
    if (arr.length > MAX_RECENT) arr = arr.slice(0, MAX_RECENT);
    cookies.set(RECENT_COOKIE_KEY, arr, { path: "/" });
  };

  const recentCompanyIds = getRecentCompanies();
  const recentCompanies = recentCompanyIds
    .map((id) => companies.find((c) => c.companyId.toString() === id))
    .filter(Boolean);

  const allOtherCompanies = companies.filter((c) => !recentCompanyIds.includes(c.companyId.toString()));

  useEffect(() => {
    const cookie = cookies.get("choosenCompany");
    if (!cookie || cookie !== true) {
      if (companies && companies.length > 1) setIsCompanyChoiceOpen(true);
    }
  }, [companies, cookies]);

  const handleSelect = async (companyId: string) => {
    const currentCompany = companies.find((c: any) => String(c.companyId) === companyId);
    cookies.set("choosenCompany", "true", { path: "/" });
    setRecentCompanies(companyId);
    setIsCompanyChoiceOpen(false);
    setValue(companyId);
    if (currentCompany) {
      await changeCompany(currentCompany.companyId);
    }
    combobox.closeDropdown();
  };

  const selectedOption = companies.find((item) => item.companyId.toString() === value);

  return (
    <Modal
      opened={isCompanyChoiceOpen}
      onClose={() => setIsCompanyChoiceOpen(false)}
      withCloseButton={false}
      centered
      radius={0}
      size={"xl"}
      title={
        <Group>
          <SVGAsset name={"companyXl-white"} className="circle" style={{ flexShrink: 0 }} />
          <Text>{t("actions:chooseCompanyToTradeFor")}</Text>
        </Group>
      }
    >
      <Stack p={30} align={"center"} justify={"center"}>
        <Text>{t("navigation:companySelectInfo")}</Text>
        <Combobox
          store={combobox}
          withinPortal={true}
          onOptionSubmit={async (val) => {
            await handleSelect(val);
            combobox.closeDropdown();
          }}
        >
          <Combobox.Target data-testid="company-select-trigger">
            <InputBase
              component="button"
              type="button"
              pointer
              mt={20}
              maw={500}
              w={"100%"}
              mx={"auto"}
              rightSection={<Combobox.Chevron />}
              onClick={() => combobox.toggleDropdown()}
              rightSectionPointerEvents="none"
              multiline={true}
              styles={{
                input: {
                  minWidth: 0,
                  maxWidth: "100%",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  display: "flex",
                  alignItems: "center",
                  paddingInlineEnd: 0,
                  paddingRight: 0,
                },
                root: {
                  minWidth: 0,
                  maxWidth: "100%",
                },
              }}
            >
              {selectedOption ? (
                <SelectOption {...selectedOption} highlight />
              ) : (
                <Input.Placeholder>{t("actions:chooseCompany")}</Input.Placeholder>
              )}
            </InputBase>
          </Combobox.Target>

          <Combobox.Dropdown style={{ maxHeight: 400, overflowY: "auto", minWidth: 320 }}>
            <Combobox.Options p={10}>
              {companies.length >= 3 && recentCompanies.length > 0 && (
                <>
                  <Group gap={6} mb={4} align="center">
                    <Text size="xs" fw={600} c={"muted"}>
                      {t("company:recentCompanies")}
                    </Text>
                  </Group>
                  <Stack gap={2}>
                    {recentCompanies.map((item: any) => (
                      <Combobox.Option
                        value={item.companyId.toString()}
                        key={"recent-" + item.companyId}
                        data-testid={"recent-company-" + item.companyId}
                      >
                        <SelectOption {...item} highlight />
                      </Combobox.Option>
                    ))}
                  </Stack>
                  <Divider my={4} />
                </>
              )}
              <Group gap={6} mb={4} align="center">
                <Text size="xs" fw={600} c={"muted"}>
                  {t("company:allCompanies")}
                </Text>
              </Group>
              <Stack gap={0}>
                {allOtherCompanies.map((item) => (
                  <Combobox.Option
                    value={item.companyId.toString()}
                    key={item.companyId}
                    data-testid={"company-" + item.companyId}
                  >
                    <SelectOption {...item} />
                  </Combobox.Option>
                ))}
              </Stack>
            </Combobox.Options>
          </Combobox.Dropdown>
        </Combobox>
      </Stack>
    </Modal>
  );
}
